import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, TooltipContentProps, PieLabelRenderProps } from 'recharts';
import { theme } from '../../../../../../tailwind.config';
import { IDestinationBreakdownResponse } from 'services/DashboardApi/types';
import { useResponsive } from 'containers/CRM/hooks/useResponsive';

interface IBookingsByDestinationChartProps {
  bookingsByDestinationData: IDestinationBreakdownResponse;
}

interface IChartSlice extends Record<string, unknown> {
  countryCode: string;
  countryName: string;
  bookingCount: number;
  percentage: number;
}

const SLICE_COLORS = [
  theme.colors['status-enquiry'],
  theme.colors['status-requested'],
  theme.colors['status-confirmed'],
  theme.colors['status-completed'],
  theme.colors['status-cancelled'],
  theme.colors['status-discarded'],
  theme.colors['status-light-cancelled'],
];

const OUTER_RADIUS = 110;
const DONUT_THICKNESS = 60;
const INNER_RADIUS = OUTER_RADIUS - DONUT_THICKNESS;

const formatPercent = (value: number) => `${value}%`;

const BookingsByDestinationTooltip = ({ active, payload }: Partial<TooltipContentProps<number, string>>) => {
  if (!active || !payload?.length) return null;

  const data: IChartSlice | undefined = payload?.[0]?.payload;
  if (!data) return null;

  return (
    <div className="bg-white border border-solid border-gray-40 py-[10px] px-[14px] font-pt-sans">
      <p className="font-pt-sans text-13px font-bold text-black m-0 mb-8px">{data.countryName}</p>
      <p className="font-pt-sans text-12px m-0 mb-4px text-gray-120">
        Bookings: <span className="text-black">{data.bookingCount.toLocaleString('en-US')}</span>
      </p>
      <p className="font-pt-sans text-12px m-0 text-gray-120">
        Share: <span className="text-black">{formatPercent(data.percentage)}</span>
      </p>
    </div>
  );
};

export const BookingsByDestinationChart: React.FC<IBookingsByDestinationChartProps> = ({ bookingsByDestinationData }) => {
  const { isMobile } = useResponsive();
  const { chartSlices, totalBookingsLabel } = useMemo(() => {
    const sorted: IChartSlice[] = (bookingsByDestinationData.destinations ?? [])
      .map<IChartSlice>(d => ({ ...d }))
      .sort((a, b) => b.bookingCount - a.bookingCount);

    return {
      chartSlices: sorted,
      totalBookingsLabel: bookingsByDestinationData.totalBookings.toLocaleString('en-US'),
    };
  }, [bookingsByDestinationData]);
  const isEmptyBookingsState = bookingsByDestinationData.totalBookings === 0;

  const renderSliceLabel = (props: PieLabelRenderProps) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, payload } = props;
    const slice: IChartSlice | undefined = payload;
    if (!slice?.percentage || slice.percentage < 5) return null;
    if (cx == null || cy == null || midAngle == null || innerRadius == null || outerRadius == null) return null;

    const RADIAN = Math.PI / 180;
    const radius = (innerRadius as number) + ((outerRadius as number) - (innerRadius as number)) * 0.55;
    const x = (cx as number) + radius * Math.cos(-(midAngle as number) * RADIAN);
    const y = (cy as number) + radius * Math.sin(-(midAngle as number) * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill={theme.colors['white']}
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontFamily: 'PT Sans', fontSize: 16 }}
      >
        {formatPercent(slice.percentage)}
      </text>
    );
  };

  return (
    <div className={`bg-white p-20px rounded ${isEmptyBookingsState ? 'border border-solid border-gray-20' : ''}`}>
      <p className="m-0 font-pt-sans text-[13px] leading-[22px] tracking-[0.52px] uppercase text-black flex justify-center">
        Bookings by Destination
      </p>

      <p className={`m-0 mt-[10px] font-pt-sans text-black flex justify-center ${isMobile ? 'text-[15px] leading-[normal]' : 'text-[14px] leading-[20px]'}`}>
        Total Bookings: <span className="font-bold ml-[6px]">{totalBookingsLabel}</span>
      </p>

      {isEmptyBookingsState ? (
        <div className="mt-[20px] flex justify-center pb-[20px]">
          <div className="w-[220px] h-[220px] rounded-full bg-gray-20 flex items-center justify-center">
            <div className="w-[104px] h-[104px] rounded-full bg-white" />
          </div>
        </div>
      ) : (
        <>
          <div className={`mt-[16px] flex items-center justify-center gap-[20px] ${isMobile ? 'flex-nowrap' : 'flex-wrap'}`}>
            <div className={isMobile ? 'w-[220px] h-[220px] shrink-0' : 'w-full h-[260px] max-w-[320px] shrink-0'}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartSlices}
                    dataKey="bookingCount"
                    nameKey="countryName"
                    cx="50%"
                    cy="50%"
                    innerRadius={INNER_RADIUS}
                    outerRadius={OUTER_RADIUS}
                    startAngle={90}
                    endAngle={-270}
                    stroke="none"
                    labelLine={false}
                    label={renderSliceLabel}
                    isAnimationActive={false}
                  >
                    {chartSlices.map((_, idx) => (
                      <Cell key={`slice-${idx}`} fill={SLICE_COLORS[idx % SLICE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<BookingsByDestinationTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className={`flex flex-col gap-[10px] ${isMobile ? 'items-start max-w-[220px]' : 'items-start'}`}>
              {chartSlices.map((d, idx) => (
                <div key={`${d.countryCode}-${idx}`} className="flex items-center gap-[8px]">
                  <div
                    className="w-[8px] h-[8px] rounded-full"
                    style={{ backgroundColor: SLICE_COLORS[idx % SLICE_COLORS.length] }}
                  />
                  <p className="m-0 font-pt-sans text-[13px] leading-[normal] text-black">
                    {d.countryName} <span className="font-bold">{d.bookingCount.toLocaleString('en-US')}</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

